import { injectable, injectFromBase, inject } from 'inversify'
import { DomainSettings } from '@/decorators/domainDecorators'
import DomainBaseService from '@/services/domain/domainBaseService'
import UserNumbers from '@/data/models/domain/UserNumbers'
import type UserNumberRepository from '@/data/repositories/domain/userNumbersRepository'
import type { GetUserNumbersOptions, ByocNumbersResponse } from '@/services/domain/byoc/types'
import { ByocProvider } from '@/data/models/domain/services/byoc/types'
import type SenderSource from '@/data/models/domain/SenderSource'
import { UserStatus, type ParsedUserNumbers } from '@/services/domain/user/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type ByocHelperService from '@/services/domain/byoc/byocHelperService'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import type { ModelRaw } from '@/types'
import type UserService from '@/services/domain/user/userService'

@DomainSettings({
  model: UserNumbers,
})
@injectable()
@injectFromBase()
export default class UserNumbersService extends DomainBaseService<UserNumberRepository> {
  protected providerMap: Record<ByocProvider, keyof ByocNumbersResponse> = {
    [ByocProvider.TWILIO]: 'userCarrierTwilio',
    [ByocProvider.VONAGE]: 'userCarrierVonage',
    [ByocProvider.SINCH]: 'userCarrierSinch',
    [ByocProvider.BANDWIDTH]: 'userCarrierBandwidth',
    [ByocProvider.INFOBIP]: 'userCarrierInfobip',
    [ByocProvider.MESSAGEBIRD]: 'userCarrierMessageBird',
    [ByocProvider.AWS]: 'userCarrierAws',
    [ByocProvider.TELNYX]: 'userCarrierTelnyx',
  }

  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
    @inject(SERVICE_TYPES.ByocHelperService) protected readonly byocHelperService: ByocHelperService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public async fetchAndGetParsedUserNumbers({ userId, enableByocSenderId }: GetUserNumbersOptions) {
    const userNumbers = await this.getDomainRepository().fetchUserNumbers(userId)
    return this.parseUserNumbers(userNumbers, enableByocSenderId)
  }

  public async fetchUserNumbers() {
    await this.getDomainRepository().fetchUserNumbers()
  }

  public findUserNumbersOrFail(userId?: string) {
    return this.getDomainRepository().findUserNumbersOrFail(userId)
  }

  public findUserNumbersOrNull(userId?: string) {
    return this.getDomainRepository().findUserNumbersOrNull(userId)
  }

  public getByocSenderSourceByPhone(userNumbers: UserNumbers, phone: string) {
    const senderSources = this.findByocSenderSources(userNumbers)
    return senderSources.find((senderSource) => senderSource.phone === phone)
  }

  public getSenderSourceByPhone(userNumbers: UserNumbers, phone: string) {
    const senderSources = this.getFlattenedSenderSources(userNumbers)
    return senderSources.find((senderSource) => senderSource.phone === phone)
  }

  public byocParseNumbers(
    rawNumbers: ByocNumbersResponse,
    enableByocSenderId?: boolean,
  ): Record<ByocProvider, SenderSource[]> {
    return Object.keys(this.providerMap).reduce(
      (acc, provider) => {
        const providerField = this.providerMap[provider]
        if (enableByocSenderId && this.byocHelperService.doesProviderSupportSenderId(provider as ByocProvider)) {
          const senderIdField = `${providerField}SenderId` as keyof ByocNumbersResponse
          acc[provider] = [...(rawNumbers[providerField] || []), ...(rawNumbers[senderIdField] || [])]
        } else {
          acc[provider] = rawNumbers[providerField] || []
        }

        return acc
      },
      {} as Record<ByocProvider, SenderSource[]>,
    )
  }

  public hasByocNumbers(userNumbers: UserNumbers) {
    return this.findByocSenderSources(userNumbers, true).length > 0
  }

  public hasNumbersForCurrentUserCountry(userNumbers: UserNumbers) {
    const countryId = this.userService.getCurrentUserCountryId()

    if (['US', 'CA', 'PR'].includes(countryId)) {
      return userNumbers.hasDedicatedNumbers || this.hasByocNumbers(userNumbers)
    }
    if (['GB', 'AU'].includes(countryId)) {
      return userNumbers.hasDedicatedNumbers || this.hasByocNumbers(userNumbers) || userNumbers.hasSenderIds
    }
    return true
  }

  public parseUserNumbers(rawUserNumbers: ModelRaw<UserNumbers>, enableByocSenderId?: boolean): ParsedUserNumbers {
    const { dedicated, shared, userNumbers, senderIds } = rawUserNumbers

    return {
      dedicated: dedicated || [],
      shared: shared || [],
      userNumbers: userNumbers || [],
      senderIds: senderIds || [],
      byoc: this.byocParseNumbers(rawUserNumbers, enableByocSenderId),
    }
  }

  protected findByocSenderSources(userNumbers: UserNumbers, enableSenderId?: boolean) {
    const parsedByoc = this.byocParseNumbers(userNumbers, enableSenderId)
    return Object.values(parsedByoc).flat()
  }

  protected getFlattenedSenderSources(userNumbers: UserNumbers) {
    const objectValue: unknown[] = Object.values(userNumbers)
    const filteredObjectValue: ModelRaw<SenderSource>[] = []
    objectValue.forEach((t) => {
      if (typeof t !== 'object') {
        return
      }
      filteredObjectValue.push(t as ModelRaw<SenderSource>)
    })

    return filteredObjectValue.flat()
  }

  public canSendSms(userNumbers: UserNumbers): boolean {
    const hasNumbersForCurrentUserCountry = this.hasNumbersForCurrentUserCountry(userNumbers)

    if (hasNumbersForCurrentUserCountry) {
      return true
    }

    // For old users in GB country who uses their own number
    const isGBCountry = this.userService.getCurrentUserCountryId() === 'GB'
    if (isGBCountry && userNumbers.hasVerifiedUserOwnNumber && this.hasPaidInvoices()) {
      return true
    }

    return false
  }

  private hasPaidInvoices() {
    const user = this.userService.currentUser()
    return user.status === UserStatus.ST_ACTIVE
  }

  public isSenderId(sender: ModelRaw<SenderSource>) {
    return sender.type.endsWith('SENDER_ID')
  }
}
